import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgForm } from '@angular/forms';
import { GlobalConfig, ToastrService } from 'ngx-toastr';
import { v4 as uuidv4 } from 'uuid';
import { environment } from 'environments/environment.prod';
import * as feather from 'feather-icons';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, finalize, map, switchMap, tap } from 'rxjs/operators';
import { FlatpickrOptions } from 'ng2-flatpickr';
import { French } from 'flatpickr/dist/l10n/fr.js';
import { DataService } from '../service/data.service';
import { HeaderService } from '../../../../services/header/header.service';
import { ClientService } from '../../../../services/Client/client.service';
import { PrixService, Prix } from '../../../parametre/liste-prix/prix.service';
import { Client, Telephone, Address, Information, Email } from '../../../../models/client';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {CroudComponent} from '../../../parametre/liste-prix/croud-liste-prix/croud.component';
import {
    CoordinateParamsPayload,
    ParamTablesService,
    parseCoordinateValeurFromResponse
} from '../../../../services/parametrage/param-tables.service';

/** Ligne « Paramètres des données » (param-tables / table client). */
type ClientParamDataFieldType = 'varchar' | 'date' | 'nombre' | 'select';

interface ClientParamDataRow {
    libelle: string;
    type: ClientParamDataFieldType;
    valeur: string;
    champ_db: string;
    param_table_id?: number | null;
}

interface ClientParamDataSelectOption {
    label: string;
    value: string;
}

interface ApiResponse {
    message?: string;
    client?: Client;
}
@Component({
    selector: 'app-add-client',
    templateUrl: './add-client.component.html',
    styleUrls: ['./add-client.component.scss']
})
export class AddClientComponent implements OnInit {
    @ViewChild(CroudComponent) crudComponent!: CroudComponent;
    steps: string[] = [
        'Type de client',
        'Informations du client',
        'Coordonnées',
        'Informations supplémentaires',
        'Paramètres des données',
        'Terminé'
    ];
    currentStep: number = 0;
    productData: any = {
        type: '',
        authorizedOverdraft: null,
        priceType: null,
        paymentPeriod: '',
        paymentPeriodValue: null,
        customPrices: []
    };
    prixList: Prix[] = [];
    addressArray: Address[] = [];
    isStep1Valid: boolean = false;
    isStep2Valid: boolean = false;
    isStep3Valid: boolean = true;
    isStep4Valid: boolean = true;
    isStepParamDataValid = true;
    readonly tableDbClient = 'clients';
    paramTableRows: ClientParamDataRow[] = [];
    paramTablesLoading = false;
    private readonly paramSelectOptionsCache = new Map<number, ClientParamDataSelectOption[]>();
    mode: 'add' | 'edit' = 'add';
    file: string = environment.file_url;
    showPriceModal: boolean = false;
    newPriceName: string = '';
    newPriceValue: number = 0;
    private options: GlobalConfig;
    isSaved: boolean = false;
    originalClientId: number | null = null;
    isLoading: boolean = false;
    isSubmitting: boolean = false;
    hasAddedPhone: boolean = false;
    hasAddedAddress: boolean = false;
    isAddingPrice: boolean = false;
    showPaymentErrors: boolean = false;
    client: Client = {
        type: '',
        civilite: '',
        nom_prenom: '',
        station_id: 0,
        user_id: 0,
        identifiant_unique: '',
        nom_entreprise: null,
        numero_identification_fiscale: null,
        site_web: null,
        emails: [{ email: '', user_id: 0, client_id: 0, station_id: 0 }],
        is_archive: false,
        telephones: [],
        adresses: [],
        informations: []
    };
    step2Errors = {
        civilite: false,
        nom_prenom: false,
        nom_entreprise: false
    };
    step3Errors: {
        phones: { [key: number]: {
                type?: string;
                code_pays?: string;
                number?: string;
            } };
        addresses: { [key: number]: { type?: string; pays?: string; region?: string; code_postal?: string; adresse?: string } };
    } = { phones: {}, addresses: {} };
    clientId: number | null = null;
    currentUserId: number = 0;
    currentStationId: number = 0;
    showTypeError: boolean = false;
    @ViewChild('step2Form') step2Form!: NgForm;
    @ViewChild('step3Form') step3Form!: NgForm;
    @ViewChild('step4Form') step4Form!: NgForm;
    touchedPhoneRows = new Set<number>();
    touchedAddressRows = new Set<number>();
    constructor(
        private cd: ChangeDetectorRef,
        private route: ActivatedRoute,
        private router: Router,
        private toastr: ToastrService,
        private dataService: DataService,
        private modalService: NgbModal,
        private headerService: HeaderService,
        private clientService: ClientService,
        private prixService: PrixService,
        private paramTablesService: ParamTablesService
    ) {
        this.options = this.toastr.toastrConfig;
    }

    ngOnInit(): void {
        this.loadDynamicIds();
        this.loadPrixList();

        const navigation = this.router.getCurrentNavigation();
        if (navigation?.extras?.state?.client && navigation.extras.state.isEditMode) {
            this.mode = 'edit';
            const clientData = navigation.extras.state.client as Client;
            this.clientId = clientData.id || null;
            this.populateForm(clientData);
            this.currentStep = 0;
        } else {
            this.mode = this.route.snapshot.url.some((seg) => seg.path === 'edit') ? 'edit' : 'add';
            if (this.mode === 'edit') {
                const clientId = this.route.snapshot.paramMap.get('id');
                if (clientId) {
                    this.clientId = +clientId;
                    this.originalClientId = this.clientId;
                    this.loadClientData(this.clientId);
                } else {
                    this.toastr.error('Aucun ID de client fourni pour la modification.', 'Erreur');
                    this.router.navigate(['/bibliotheque-clients/actif']);
                }
                this.currentStep = 0;
            } else {
                this.client.informations = [{
                    encours_atorise: null,
                    prix_id: null,
                    type_date: null,
                    nb_date: null,
                    client_id: this.clientId || 0,
                    station_id: this.currentStationId,
                    user_id: this.currentUserId
                }];
                this.client.telephones = [{
                    type: null,
                    num_tel: null,
                    code_pays: '',
                    station_id: this.currentStationId,
                    user_id: this.currentUserId,
                    client_id: this.clientId || 0
                }];
                this.addressArray = [{
                    type: null,
                    pays: null,
                    region: null,
                    code_postal: null,
                    adresse: null,
                    station_id: this.currentStationId,
                    user_id: this.currentUserId,
                    client_id: this.clientId || 0
                }];
                // Initialiser les champs professionnels
                this.client.nom_entreprise = null;
                this.client.numero_identification_fiscale = null;
                this.client.site_web = null;
                this.hasAddedPhone = true;
                this.hasAddedAddress = true;
            }
        }
        if (this.mode === 'edit') {
            const clientId = this.route.snapshot.paramMap.get('id');
            if (clientId) {
                this.clientId = +clientId;
                this.originalClientId = this.clientId;
                this.loadClientData(this.clientId);
            } else {
            }
            this.currentStep = 0;
        }
        this.headerService.setConfig({
            title: this.mode === 'edit' ? 'Modifier Client' : 'Ajouter Client',
            buttons: []
        });

        this.validateStep3();
        this.validateStep4();
        this.cd.markForCheck();
        this.refreshFeatherIcons();
    }

    ngAfterViewInit(): void {
        if (this.crudComponent) {
            this.crudComponent.prixAdded.subscribe(() => {
                this.loadPrixList();
                this.validateStep4();
            });

            this.crudComponent.prixUpdated.subscribe((updatedPrix: Prix) => {
                const index = this.prixList.findIndex(p => p.id === updatedPrix.id);
                if (index !== -1) {
                    this.prixList[index] = updatedPrix;
                }
                this.cd.detectChanges();
            });

            this.crudComponent.prixError.subscribe((errorType: string) => {
            });
        }
    }

    private loadPrixList(): void {
        if (this.currentStationId > 0) {
            this.prixService.getPrix(this.currentStationId).subscribe({
                next: (prixList) => {
                    this.prixList = prixList.filter(prix => prix.statut).sort((a, b) => a.id - b.id);
                    if (this.prixList.length === 0) {
                    }
                    if (this.isAddingPrice) {
                        const newPriceId = this.prixList[this.prixList.length - 1]?.id;
                        if (newPriceId) {
                            this.productData.priceType = newPriceId;
                            if (this.client.informations && this.client.informations.length > 0) {
                                this.client.informations[0].prix_id = newPriceId;
                            }
                            this.validateStep4();
                            this.cd.detectChanges();
                        }
                        this.isAddingPrice = false;
                    }
                    this.cd.detectChanges();
                },
                error: (error) => {
                    this.toastr.error('Erreur lors du chargement des prix.', 'Erreur');
                    this.prixList = [];
                    this.cd.detectChanges();
                }
            });
        } else {
            this.prixList = [];
            this.toastr.error('ID de station invalide. Veuillez vérifier votre configuration.', 'Erreur');
        }
    }

    openAddPriceModal(): void {

        if (!this.crudComponent) {
            this.toastr.error("Composant de prix non disponible. Rechargez la page.", 'Erreur');
            return;
        }

        this.isAddingPrice = true;
        this.crudComponent.stationId = this.currentStationId;
        this.crudComponent.clientIdFromParent = this.clientId;
        this.crudComponent.openModal('ajouter');
    }

    private loadDynamicIds(): void {
        try {
            const user = localStorage.getItem('currentUser');
            if (user) {
                const currentUser = JSON.parse(user);
                this.currentUserId = currentUser?.id || 5;
                this.currentStationId = currentUser?.stations?.[0]?.id || 2;
                this.client.user_id = this.currentUserId;
                this.client.station_id = this.currentStationId;
                this.addressArray = [];
                this.client.emails = [{
                    email: '',
                    user_id: this.currentUserId,
                    client_id: this.clientId || 0,
                    station_id: this.currentStationId
                }];
                // Initialiser les champs professionnels
                this.client.nom_entreprise = null;
                this.client.numero_identification_fiscale = null;
                this.client.site_web = null;
            } else {
                this.currentUserId = 5;
                this.currentStationId = 2;
                this.client.user_id = this.currentUserId;
                this.client.station_id = this.currentStationId;
                this.addressArray = [];
                this.client.emails = [{
                    email: '',
                    user_id: this.currentUserId,
                    client_id: this.clientId || 0,
                    station_id: this.currentStationId
                }];
                // Initialiser les champs professionnels
                this.client.nom_entreprise = null;
                this.client.numero_identification_fiscale = null;
                this.client.site_web = null;

                this.toastr.warning('Utilisateur non trouvé dans localStorage. Utilisation des IDs par défaut.', 'Attention');
            }
        } catch (error) {
            this.currentUserId = 5;
            this.currentStationId = 2;
            this.client.user_id = this.currentUserId;
            this.client.station_id = this.currentStationId;
            this.addressArray = [];
            this.client.emails = [{
                email: '',
                user_id: this.currentUserId,
                client_id: this.clientId || 0,
                station_id: this.currentStationId
            }];
            // Initialiser les champs professionnels
            this.client.nom_entreprise = null;
            this.client.numero_identification_fiscale = null;
            this.client.site_web = null;
            this.toastr.error('Erreur lors de la récupération des IDs utilisateur/station.', 'Erreur');
        }
    }

    private loadClientData(clientId: number): void {
        this.isLoading = true;
        this.clientService.getClientById(clientId).subscribe({
            next: (clientData) => {
                this.populateForm(clientData);
                this.isLoading = false;
            },
            error: (error) => {
                this.isLoading = false;
                this.toastr.error('Erreur lors du chargement des données du client.', 'Erreur');
                this.router.navigate(['/bibliotheque-clients/actif']);
            }
        });
    }

    trackByIndex(index: number): number {
        return index;
    }

    populateForm(clientData: Client): void {
        let emails: Email[] = [];
        if (clientData.emails) {
            if (Array.isArray(clientData.emails) && typeof clientData.emails[0] === 'string') {
                emails = clientData.emails.map(email => ({
                    email: email as string,
                    user_id: this.currentUserId,
                    client_id: clientData.id || this.clientId || 0,
                    station_id: this.currentStationId,
                    id: undefined,
                    created_at: undefined,
                    updated_at: undefined
                } as Email));
            } else {
                emails = (clientData.emails as Email[]).map(email => ({
                    id: email.id || undefined,
                    email: email.email || '',
                    user_id: this.currentUserId,
                    client_id: clientData.id || this.clientId || 0,
                    station_id: this.currentStationId,
                    created_at: email.created_at || undefined,
                    updated_at: email.updated_at || undefined
                } as Email));
            }
        }
        if (emails.length === 0) {
            emails = [{ email: '', user_id: this.currentUserId, client_id: clientData.id || this.clientId || 0, station_id: this.currentStationId }];
        }

        let telephones: Telephone[] = [];
        if (clientData.telephones?.length > 0) {
            telephones = clientData.telephones.map(tel => ({
                id: tel.id || undefined,
                type: tel.type === 'telephonePortable' ? 'mobile' : tel.type || null,
                num_tel: tel.num_tel || null,
                code_pays: tel.code_pays || null,
                station_id: this.currentStationId,
                user_id: this.currentUserId,
                client_id: clientData.id || this.clientId || 0,
                created_at: tel.created_at || undefined,
                updated_at: tel.updated_at || undefined
            } as Telephone));
        } else {
            telephones = [{
                type: null,
                num_tel: null,
                code_pays: null,
                station_id: this.currentStationId,
                user_id: this.currentUserId,
                client_id: clientData.id || this.clientId || 0
            }];
        }

        let addresses: Address[] = [];
        if (clientData.adresses?.length > 0) {
            addresses = clientData.adresses.map(addr => ({
                id: addr.id || undefined,
                type: addr.type === 'adresse_fac' ? 'facturation' : addr.type === 'adresse_li' ? 'livraison' : addr.type || null,
                pays: addr.pays === 'FR' ? 'France' : addr.pays === 'BE' ? 'Belgique' : addr.pays === 'CH' ? 'Suisse' : addr.pays === 'MA' ? 'Maroc' : addr.pays || null,
                region: addr.region || null,
                code_postal: addr.code_postal || null,
                adresse: addr.adresse || null,
                station_id: this.currentStationId,
                user_id: this.currentUserId,
                client_id: clientData.id || this.clientId || 0,
                created_at: addr.created_at || undefined,
                updated_at: addr.updated_at || undefined
            } as Address));
        } else {
            addresses = [{
                type: null,
                pays: null,
                region: null,
                code_postal: null,
                adresse: null,
                station_id: this.currentStationId,
                user_id: this.currentUserId,
                client_id: this.clientId || 0
            }];
        }

        this.client = {
            id: clientData.id || undefined,
            type: clientData.type || '',
            civilite: clientData.civilite || '',
            nom_prenom: clientData.nom_prenom || '',
            station_id: clientData.station_id || this.currentStationId,
            user_id: clientData.user_id || this.currentUserId,
            identifiant_unique: clientData.identifiant_unique || '',
            nom_entreprise: clientData.nom_entreprise || null,
            numero_identification_fiscale: clientData.numero_identification_fiscale || null,
            site_web: clientData.site_web || null,
            emails: emails,
            is_archive: clientData.is_archive || false,
            created_at: clientData.created_at || undefined,
            updated_at: clientData.updated_at || undefined,
            telephones: telephones,
            adresses: addresses,
            informations: clientData.informations?.length > 0
                ? clientData.informations.map(info => ({
                    id: info.id || undefined,
                    encours_atorise: info.encours_atorise || null,
                    prix_id: info.prix_id || null,
                    type_date: info.type_date || null,
                    nb_date: info.nb_date || null,
                    client_id: clientData.id || this.clientId || 0,
                    station_id: this.currentStationId,
                    user_id: this.currentUserId,
                    created_at: info.created_at || undefined,
                    updated_at: info.updated_at || undefined
                } as Information))
                : [{
                    encours_atorise: null,
                    prix_id: null,
                    type_date: null,
                    nb_date: null,
                    client_id: clientData.id || this.clientId || 0,
                    station_id: this.currentStationId,
                    user_id: this.currentUserId
                }],
        };
        this.clientId = clientData.id || null;
        this.productData = {
            type: clientData.type === 'Individuel' ? 'Materiel' : clientData.type === 'Professionnel' ? 'Service' : '',
            authorizedOverdraft: clientData.informations?.[0]?.encours_atorise || null,
            priceType: clientData.informations?.[0]?.prix_id || null,
            paymentPeriod: clientData.informations?.[0]?.type_date || '',
            paymentPeriodValue: clientData.informations?.[0]?.nb_date || null,
            customPrices: clientData.informations?.[0]?.prix_id
                ? [{ id: uuidv4(), prix_id: clientData.informations[0].prix_id, name: this.getPriceLibelle(clientData.informations[0].prix_id), value: 0 }]
                : []
        };
        this.addressArray = this.client.adresses;
        this.hasAddedPhone = this.client.telephones.length > 0;
        this.hasAddedAddress = this.addressArray.length > 0;
        this.validateStep1();
        this.validateStep2();
        this.validateStep3();
        this.validateStep4();
        this.cd.markForCheck();
    }

    private getPriceLibelle(prixId: number | null): string {
        if (!prixId) return '';
        const prix = this.prixList.find(p => p.id === prixId);
        return prix ? prix.libelle : 'Prix inconnu';
    }

    refreshFeatherIcons(): void {
        feather.replace();
        this.cd.detectChanges();
    }

    onTimelineStepSelected(stepIndex: number): void {
        if (stepIndex >= 0 && stepIndex < this.steps.length && this.isPreviousStepsValid(stepIndex)) {
            this.currentStep = stepIndex;
            if (stepIndex === 4) {
                this.loadClientParamTables();
            }
            this.cd.detectChanges();
            this.refreshFeatherIcons();
        }
    }

    isPreviousStepsValid(stepIndex: number): boolean {
        for (let i = 0; i < stepIndex; i++) {
            switch (i) {
                case 0:
                    if (!this.isStep1Valid) return false;
                    break;
                case 1:
                    if (!this.isStep2Valid) return false;
                    break;
                case 2:
                    if (!this.isStep3Valid) return false;
                    break;
                case 3:
                    if (!this.isStep4Valid) return false;
                    break;
                case 4:
                    if (!this.isStepParamDataValid) return false;
                    break;
            }
        }
        return true;
    }

    nextStep(): void {
        if (this.isCurrentStepValid() && this.currentStep < this.steps.length - 1) {
            if (this.currentStep === 2 && this.prixList.length === 0) {
                return;
            }
            this.currentStep++;
            if (this.currentStep === 4) {
                this.loadClientParamTables();
            }
            this.cd.detectChanges();
            this.refreshFeatherIcons();
        }
    }

    goBack(): void {
        if (this.currentStep > 0) {
            this.currentStep--;
            this.cd.detectChanges();
        } else {
            this.router.navigate(['/bibliotheque-clients/actif']);
        }
    }

    retour(): void {
        this.router.navigate(['/bibliotheque-clients/actif']);
    }

    isCurrentStepValid(): boolean {
        switch (this.currentStep) {
            case 0:
                return this.isStep1Valid;
            case 1:
                return this.isStep2Valid;
            case 2:
                return this.isStep3Valid;
            case 3:
                return this.isStep4Valid;
            case 4:
                return this.isStepParamDataValid;
            case 5:
                return true;
            default:
                return false;
        }
    }

    validateStep1(): void {
        this.isStep1Valid = !!this.productData.type && !!this.client.type;
        this.cd.markForCheck();
    }

    validateStep2(): void {
        // Pour les clients professionnels, nom_entreprise est requis
        if (this.client.type === 'Professionnel') {
            this.isStep2Valid = !!this.client.type && !!this.client.civilite && !!this.client.nom_prenom && !!this.client.nom_entreprise;
        } else {
            this.isStep2Valid = !!this.client.type && !!this.client.civilite && !!this.client.nom_prenom;
        }
        this.cd.markForCheck();
    }
    validateStep2Visual(): void {
        this.step2Errors.civilite = !this.client.civilite;
        this.step2Errors.nom_prenom = !this.client.nom_prenom || this.client.nom_prenom.trim() === '';
        // Ajouter validation pour nom_entreprise si professionnel
        if (this.client.type === 'Professionnel') {
            this.step2Errors.nom_entreprise = !this.client.nom_entreprise || this.client.nom_entreprise.trim() === '';
        }
        this.cd.detectChanges();
    }

    validateStep3(): void {
        let phoneHasError = false;
        let addressHasError = false;

        this.client.telephones.forEach((phone, index) => {
            const isTouched = this.hasAnyPhoneFieldFilled(index);

            if (isTouched) {
                const hasCodePays = !!phone.code_pays && phone.code_pays.trim() !== '';
                const hasType = !!phone.type;
                const hasValidNumber = phone.num_tel && /^[1-9][0-9]{7}$/.test(phone.num_tel.toString().trim());

                if (!hasCodePays || !hasType || !hasValidNumber) {
                    phoneHasError = true;
                }
            }
        });

        this.addressArray.forEach((addr, index) => {
            const isTouched = this.hasAnyAddressFieldFilled(index);
            if (isTouched) {
                if (!addr.type || !addr.pays || !addr.region || !addr.code_postal || !addr.adresse) {
                    addressHasError = true;
                }
            }
        });

        this.isStep3Valid = !phoneHasError && !addressHasError;
        this.cd.markForCheck();
    }

    validatePhoneVisual(): void {
        this.client.telephones.forEach((phone, index) => {
            const isTouched = this.hasAnyPhoneFieldFilled(index);
            this.step3Errors.phones[index] = {};

            if (isTouched) {
                if (!phone.code_pays || phone.code_pays.trim() === '') {
                    this.step3Errors.phones[index].code_pays = 'Le code pays est obligatoire';
                }

                if (!phone.type) {
                    this.step3Errors.phones[index].type = 'Le type de téléphone est obligatoire';
                }

                if (!phone.num_tel) {
                    this.step3Errors.phones[index].number = 'Le numéro est obligatoire';
                } else {
                    const numStr = phone.num_tel.toString().trim();
                    if (!/^[1-9][0-9]{7}$/.test(numStr)) {
                        this.step3Errors.phones[index].number = '8 chiffres requis (ex: 22123456)';
                    }
                }
            }
        });

        this.validateStep3();
        this.cd.detectChanges();
    }

    validateAddressVisual(): void {
        this.addressArray.forEach((addr, index) => {
            const isTouched = this.hasAnyAddressFieldFilled(index);
            this.step3Errors.addresses[index] = {};

            if (isTouched) {
                if (!addr.type) this.setAddressError(index, 'type', 'Sélectionnez le type d’adresse');
                if (!addr.pays) this.setAddressError(index, 'pays', 'Le pays est obligatoire');
                if (!addr.region) this.setAddressError(index, 'region', 'La région est obligatoire');
                if (!addr.code_postal) this.setAddressError(index, 'code_postal', 'Le code postal est obligatoire');
                if (!addr.adresse) this.setAddressError(index, 'adresse', 'L’adresse complète est obligatoire');
            }
        });
        this.validateStep3();
        this.cd.detectChanges();
    }
    validateStep4(): void {
        this.isStep4Valid = true;

        if (this.productData.authorizedOverdraft !== null && this.productData.authorizedOverdraft !== '') {
            const num = Number(this.productData.authorizedOverdraft);
            if (isNaN(num) || num < 0) {
                this.isStep4Valid = false;
            }
        }

        if (this.showPaymentErrors) {
            const hasPeriod = !!this.productData.paymentPeriod;
            const hasValue = this.productData.paymentPeriodValue != null && this.productData.paymentPeriodValue > 0;

            if ((hasPeriod && !hasValue) || (!hasPeriod && hasValue)) {
                this.isStep4Valid = false;
            }
        }

        const hasOverdraft = this.productData.authorizedOverdraft !== null && this.productData.authorizedOverdraft !== '';
        const hasPayment = !!this.productData.paymentPeriod && this.productData.paymentPeriodValue > 0;

        if ((hasOverdraft || hasPayment) && !this.productData.priceType) {
            this.isStep4Valid = false;
        }


        if (this.productData.priceType && !this.prixList.some(p => p.id === this.productData.priceType)) {
            this.isStep4Valid = false;
        }

        this.cd.markForCheck();
    }

    /**
     * Même résolution que add-product pour `getParamTablesByTable` / select-params / coordinate-params :
     * `station_id` = première station (`station_id` ou `id`) ou `station_id` racine ;
     * `user_id` = `id` utilisateur (dans add-product ce membre est nommé `clientId`).
     */
    private getParamTablesApiIds(): { stationId: number; userId: number } {
        let currentUser: any = null;
        try {
            const raw = localStorage.getItem('currentUser');
            if (raw) {
                currentUser = JSON.parse(raw);
            }
        } catch {
            currentUser = null;
        }
        const stationId = Number(
            currentUser?.stations?.[0]?.station_id ??
                currentUser?.stations?.[0]?.id ??
                currentUser?.station_id ??
                this.currentStationId ??
                0
        );
        const userId = Number(currentUser?.id ?? this.currentUserId ?? 0);
        return { stationId, userId };
    }

    loadClientParamTables(): void {
        const { stationId: sid, userId: uid } = this.getParamTablesApiIds();
        if (!Number.isFinite(sid) || sid <= 0 || !Number.isFinite(uid) || uid <= 0) {
            this.toastr.warning('Station ou utilisateur introuvable pour charger les paramètres.', 'Attention');
            this.paramTableRows = [this.createEmptyClientParamRow(0)];
            return;
        }
        this.paramSelectOptionsCache.clear();
        this.paramTablesLoading = true;
        this.paramTablesService.getParamTablesByTable(this.tableDbClient, sid, uid).pipe(
            switchMap((raw) => {
                let rows = this.normalizeClientParamTableResponse(raw);
                if (rows.length === 0) {
                    rows = [this.createEmptyClientParamRow(0)];
                }
                const cid = this.resolveClientIdForCoordinateValeur();
                return this.applyClientCoordinateValeursFromApi$(rows, sid, uid, cid).pipe(
                    map((withVals) => (withVals.length === 0 ? [this.createEmptyClientParamRow(0)] : withVals)),
                    switchMap((finalRows) => this.loadClientSelectParamOptionsForRows$(finalRows, sid, uid))
                );
            }),
            finalize(() => {
                this.paramTablesLoading = false;
                this.cd.detectChanges();
            })
        ).subscribe({
            next: (rows) => {
                this.paramTableRows = rows;
                this.validateClientParamStep();
            },
            error: () => {
                this.toastr.error('Impossible de charger les paramètres des données.');
                this.paramTableRows = [this.createEmptyClientParamRow(0)];
            }
        });
    }

    private resolveClientIdForCoordinateValeur(): number | null {
        if (this.clientId != null && Number.isFinite(this.clientId) && this.clientId > 0) {
            return Math.floor(this.clientId);
        }
        const cid = this.client?.id;
        if (cid != null && Number.isFinite(Number(cid)) && Number(cid) > 0) {
            return Math.floor(Number(cid));
        }
        return null;
    }

    private applyClientCoordinateValeursFromApi$(
        rows: ClientParamDataRow[],
        stationId: number,
        userId: number,
        clientId: number | null
    ): Observable<ClientParamDataRow[]> {
        const tableIds = [
            ...new Set(
                rows
                    .map((r) => r.param_table_id)
                    .filter((id): id is number => id != null && id > 0)
            )
        ];
        if (tableIds.length === 0 || clientId == null || clientId <= 0) {
            return of(rows);
        }
        return forkJoin(
            tableIds.map((ptid) =>
                this.paramTablesService
                    .getCoordinateParamsByParamTableClient(ptid, clientId, stationId, userId)
                    .pipe(catchError(() => of(null)))
            )
        ).pipe(
            map((responses) => {
                const byParamTableId = new Map<number, string>();
                tableIds.forEach((ptid, idx) => {
                    const raw = responses[idx];
                    byParamTableId.set(ptid, parseCoordinateValeurFromResponse(raw, clientId, 'client_id'));
                });
                return rows.map((r) => {
                    if (r.param_table_id == null || r.param_table_id <= 0) {
                        return r;
                    }
                    const v = byParamTableId.get(r.param_table_id);
                    return v !== undefined ? { ...r, valeur: v } : r;
                });
            })
        );
    }

    getClientParamSelectOptions(paramTableId: number | null | undefined): ClientParamDataSelectOption[] {
        if (paramTableId == null || !Number.isFinite(Number(paramTableId)) || Number(paramTableId) <= 0) {
            return [];
        }
        return this.paramSelectOptionsCache.get(Math.floor(Number(paramTableId))) ?? [];
    }

    private loadClientSelectParamOptionsForRows$(
        rows: ClientParamDataRow[],
        stationId: number,
        userId: number
    ): Observable<ClientParamDataRow[]> {
        const ids = [
            ...new Set(
                rows
                    .filter((r) => r.type === 'select' && r.param_table_id != null && r.param_table_id > 0)
                    .map((r) => Math.floor(r.param_table_id as number))
            )
        ];
        if (ids.length === 0) {
            return of(rows);
        }
        return forkJoin(
            ids.map((ptid) =>
                this.paramTablesService.getSelectParamsByParamTable(ptid, stationId, userId).pipe(
                    map((raw) => ({ ptid, opts: this.parseClientSelectParamsResponse(raw) })),
                    catchError(() => of({ ptid, opts: [] as ClientParamDataSelectOption[] }))
                )
            )
        ).pipe(
            tap((pairs) => {
                pairs.forEach(({ ptid, opts }) => this.paramSelectOptionsCache.set(ptid, opts));
            }),
            map(() => rows)
        );
    }

    private parseClientSelectParamsResponse(raw: unknown): ClientParamDataSelectOption[] {
        const list = this.unwrapClientSelectParamsList(raw);
        const out: ClientParamDataSelectOption[] = [];
        for (const item of list) {
            if (!item || typeof item !== 'object') {
                continue;
            }
            const o = item as Record<string, unknown>;
            const lib = String(o['libelle'] ?? o['label'] ?? o['nom'] ?? '').trim();
            const valueRaw = o['value'] ?? o['valeur'] ?? o['code'] ?? o['id'] ?? o['select_param_id'];
            const val = valueRaw != null && valueRaw !== '' ? String(valueRaw).trim() : '';
            const label = lib || val;
            const value = val || lib;
            if (label === '' && value === '') {
                continue;
            }
            out.push({ label: label || value, value: value || label });
        }
        return out;
    }

    private unwrapClientSelectParamsList(raw: unknown): unknown[] {
        if (Array.isArray(raw)) {
            return raw;
        }
        if (raw && typeof raw === 'object') {
            const o = raw as Record<string, unknown>;
            const cand =
                o['data'] ??
                o['rows'] ??
                o['items'] ??
                o['options'] ??
                o['select_params'] ??
                o['results'];
            if (Array.isArray(cand)) {
                return cand;
            }
            if (cand && typeof cand === 'object' && !Array.isArray(cand)) {
                const inner = (cand as Record<string, unknown>)['rows'] ?? (cand as Record<string, unknown>)['data'];
                if (Array.isArray(inner)) {
                    return inner;
                }
            }
        }
        return [];
    }

    private createEmptyClientParamRow(index: number): ClientParamDataRow {
        return {
            libelle: '',
            type: 'varchar',
            valeur: '',
            champ_db: `param${index + 1}`,
            param_table_id: null
        };
    }

    private normalizeClientParamTableResponse(raw: unknown): ClientParamDataRow[] {
        const anyRaw = raw as Record<string, unknown> | unknown[] | null | undefined;
        let list: unknown[] = [];
        if (Array.isArray(anyRaw)) {
            list = anyRaw;
        } else if (anyRaw && typeof anyRaw === 'object') {
            const o = anyRaw as Record<string, unknown>;
            const cand =
                o['data'] ??
                o['rows'] ??
                o['param_tables'] ??
                o['parametrage'] ??
                o['items'];
            list = Array.isArray(cand) ? cand : [];
        }
        const rows: ClientParamDataRow[] = [];
        for (let i = 0; i < list.length; i++) {
            const row = list[i] as Record<string, unknown>;
            if (!row || typeof row !== 'object') {
                continue;
            }
            const lib =
                (row['libelle_champ'] ?? row['libelle'] ?? row['label'] ?? '') as string;
            const typeRaw = (row['type'] ??
                row['type_champ'] ??
                row['champ_type'] ??
                row['param_type'] ??
                row['data_type'] ??
                'varchar') as string;
            const typeNorm = this.normalizeClientParamType(typeRaw);
            const champ =
                (row['champ_db'] ?? row['champDb'] ?? `param${i + 1}`) as string;
            const rawPid =
                row['id'] ?? row['param_table_id'] ?? row['paramTableId'] ?? row['parametrage_table_id'];
            let param_table_id: number | null = null;
            if (rawPid != null && rawPid !== '') {
                const n = Number(rawPid);
                if (Number.isFinite(n) && n > 0) {
                    param_table_id = Math.floor(n);
                }
            }
            rows.push({
                libelle: lib != null ? String(lib) : '',
                type: typeNorm,
                valeur: '',
                champ_db: champ != null ? String(champ) : `param${i + 1}`,
                param_table_id
            });
        }
        return rows;
    }

    private normalizeClientParamType(t: string): ClientParamDataFieldType {
        const s = (t || '').toLowerCase().trim();
        if (s === 'date' || s === 'datetime') {
            return 'date';
        }
        if (
            s === 'nombre' ||
            s === 'number' ||
            s === 'double' ||
            s === 'numeric' ||
            s === 'int' ||
            s === 'integer' ||
            s === 'float' ||
            s === 'decimal'
        ) {
            return 'nombre';
        }
        if (s === 'select' || s === 'liste' || s === 'dropdown' || s === 'list') {
            return 'select';
        }
        return 'varchar';
    }

    validateClientParamStep(): void {
        this.isStepParamDataValid = true;
    }

    readonly clientParamDateFlatpickrOptions: FlatpickrOptions = {
        locale: French,
        altInput: true,
        altFormat: 'd/m/Y',
        dateFormat: 'Y-m-d',
        allowInput: true,
        disableMobile: true,
        monthSelectorType: 'dropdown',
        altInputClass: 'form-control',
        onReady: (_selectedDates, _dateStr, instance) => {
            instance.calendarContainer.classList.add('tier-param-fp-cal');
        }
    };

    private formatClientParamLocalDateYmd(d: Date): string {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    }

    onClientParamDateValeurChange(row: ClientParamDataRow, value: unknown): void {
        if (value == null || value === '') {
            row.valeur = '';
            return;
        }
        if (Array.isArray(value) && value[0] instanceof Date) {
            row.valeur = this.formatClientParamLocalDateYmd(value[0]);
            return;
        }
        if (value instanceof Date) {
            row.valeur = this.formatClientParamLocalDateYmd(value);
            return;
        }
        row.valeur = String(value).trim();
    }

    private buildClientCoordinatePayloads(clientId: number): CoordinateParamsPayload[] {
        const { stationId: sid, userId: uid } = this.getParamTablesApiIds();
        if (!Number.isFinite(sid) || sid <= 0 || !Number.isFinite(uid) || uid <= 0) {
            return [];
        }
        if (!Number.isFinite(clientId) || clientId <= 0) {
            return [];
        }
        const out: CoordinateParamsPayload[] = [];
        for (const row of this.paramTableRows) {
            const ptid = row.param_table_id;
            if (ptid == null || ptid <= 0) {
                continue;
            }
            const v = (row.valeur ?? '').trim();
            if (v === '') {
                continue;
            }
            out.push({
                station_id: Math.floor(sid),
                user_id: Math.floor(uid),
                param_table_id: Math.floor(ptid),
                client_id: Math.floor(clientId),
                valeur: v
            });
        }
        return out;
    }

    private saveClientCoordinateParams$(clientId: number): Observable<unknown | unknown[] | null> {
        const payloads = this.buildClientCoordinatePayloads(clientId);
        if (payloads.length === 0) {
            return of(null);
        }
        return this.paramTablesService.postCoordinateParams(payloads);
    }

    handleStepCompletion(stepIndex: number): void {
        if (stepIndex === 0) {
            this.validateStep1();
            if (!this.isStep1Valid) {
                this.showTypeError = true;

                return;
            }
            this.nextStep();
        } else if (stepIndex === 1) {
            this.validateStep2();
            this.validateStep2Visual();
            if (!this.isStep2Valid) {
                return;
            }
            this.resetStep2Errors();
            this.nextStep();
        } else if (stepIndex === 2) {
            this.validateStep3();
            this.validatePhoneVisual();
            this.validateAddressVisual();
            if (!this.isStep3Valid) {
                return;
            }
            this.resetStep3Errors();
            this.nextStep();
        } else if (stepIndex === 3) {
            this.showPaymentErrors = true;

            this.validateStep4();

            if (!this.isStep4Valid) {
                return;
            }

            this.nextStep();
        } else if (stepIndex === 4) {
            this.validateClientParamStep();
            if (!this.isStepParamDataValid) {
                return;
            }
            if (!this.isSubmitting) {
                this.saveClient(false);
            }
        }
    }

    saveClient(_autoNavigateToView: boolean = false): void {
        if (this.isSubmitting) {
            return;
        }
        this.isSubmitting = true;
        this.isLoading = true;
        this.isSaved = false;

        if (this.currentUserId <= 0 || this.currentStationId <= 0) {
            this.isLoading = false;
            this.isSubmitting = false;
            this.toastr.error('ID utilisateur ou station invalide. Veuillez vous reconnecter.', 'Erreur');
            return;
        }

        // Validation des champs requis
        if (!this.client.nom_prenom || !this.client.type || !this.client.civilite) {
            this.isLoading = false;
            this.isSubmitting = false;
            this.toastr.error('Veuillez remplir tous les champs requis : type, civilité, nom et prénom.', 'Erreur');
            return;
        }
        
        // Pour les clients professionnels, vérifier que nom_entreprise est rempli
        if (this.client.type === 'Professionnel' && (!this.client.nom_entreprise || this.client.nom_entreprise.trim() === '')) {
            this.isLoading = false;
            this.isSubmitting = false;
            this.toastr.error('Veuillez remplir le nom de l\'entreprise pour les clients professionnels.', 'Erreur');
            return;
        }

        this.validateStep4();
        if (!this.isStep4Valid) {
            this.isLoading = false;
            this.isSubmitting = false;
            this.toastr.error('Veuillez corriger les erreurs dans les informations supplémentaires.', 'Erreur');
            return;
        }

        if (this.productData.authorizedOverdraft || this.productData.paymentPeriod) {
            if (!this.productData.priceType) {
                this.isLoading = false;
                this.isSubmitting = false;
                this.toastr.error('Veuillez sélectionner un type de prix.', 'Erreur');
                return;
            }
        }

        if (this.productData.priceType && !this.prixList.some(prix => prix.id === this.productData.priceType)) {
            this.isLoading = false;
            this.isSubmitting = false;
            this.toastr.error('Le type de prix sélectionné est invalide ou non trouvé dans la liste des prix.', 'Erreur');
            return;
        }

        const clientData: any = {
            id: this.clientId || undefined,
            type: this.client.type,
            civilite: this.client.civilite,
            nom_prenom: this.client.nom_prenom,
            station_id: this.currentStationId,
            user_id: this.currentUserId,
            identifiant_unique: this.client.identifiant_unique || null,
            nom_entreprise: this.client.nom_entreprise || null,
            numero_identification_fiscale: this.client.numero_identification_fiscale || null,
            site_web: this.client.site_web || null,
            emails: this.client.emails
                .filter(email => email.email)
                .map(email => email.email),
            is_archive: this.client.is_archive || false,
            created_at: this.client.created_at || undefined,
            updated_at: new Date().toISOString(),
            telephones: this.client.telephones
                .filter(phone => phone.num_tel)
                .map(phone => ({
                    id: phone.id || undefined,
                    type: phone.type || null,
                    num_tel: phone.num_tel ? String(phone.num_tel) : null,
                    code_pays: phone.code_pays || null,
                    station_id: this.currentStationId,
                    user_id: this.currentUserId,
                    client_id: this.mode === 'add' ? null : this.clientId || 0,
                    created_at: phone.created_at || new Date().toISOString(),
                    updated_at: new Date().toISOString()
                } as Telephone)),
            adresses: this.addressArray
                .filter(addr => addr.pays || addr.region || addr.code_postal || addr.adresse)
                .map(addr => ({
                    id: addr.id || undefined,
                    type: addr.type ? (addr.type === 'facturation' ? 'adresse_fac' : addr.type === 'livraison' ? 'adresse_li' : addr.type) : null,
                    pays: addr.pays ? (addr.pays === 'France' ? 'FR' : addr.pays === 'Belgique' ? 'BE' : addr.pays === 'Suisse' ? 'CH' : addr.pays === 'Maroc' ? 'MA' : addr.pays) : null,
                    region: addr.region || null,
                    code_postal: addr.code_postal || null,
                    adresse: addr.adresse || null,
                    station_id: this.currentStationId,
                    user_id: this.currentUserId,
                    client_id: this.clientId || 0,
                    created_at: addr.created_at || new Date().toISOString(),
                    updated_at: new Date().toISOString()
                } as Address)),
            information: (this.productData.authorizedOverdraft || this.productData.priceType || this.productData.paymentPeriod) ? {
                id: this.client.informations[0].id || undefined,
                encours_atorise: this.productData.authorizedOverdraft || null,
                prix_id: this.productData.priceType,
                type_date: this.productData.paymentPeriod || null,
                nb_date: this.productData.paymentPeriod ? (this.productData.paymentPeriodValue || 1) : null,
                client_id: this.clientId || 0,
                station_id: this.currentStationId,
                user_id: this.currentUserId,
                created_at: this.client.informations[0].created_at || new Date().toISOString(),
                updated_at: new Date().toISOString()
            } : undefined
        };

        const action = this.mode === 'edit' && this.clientId !== null
            ? this.clientService.updateClient(this.clientId, clientData)
            : this.clientService.addClient(clientData);

        action.pipe(
            switchMap((response: any) => {
                const typedResponse: ApiResponse = response;
                const apiClientId = typedResponse.client?.id;
                const newId = apiClientId || (this.mode === 'edit' ? this.originalClientId : null);

                if (newId) {
                    this.clientId = newId;
                    this.client = { ...this.client, id: newId };
                } else {
                    if (this.mode === 'edit' && this.originalClientId) {
                        this.clientId = this.originalClientId;
                    } else {
                        this.toastr.error('ID client non récupéré. Veuillez recharger la page.', 'Erreur');
                        return of({ __skipSuccess: true as const });
                    }
                }

                const cid = this.clientId;
                if (cid == null || !Number.isFinite(Number(cid)) || Number(cid) <= 0) {
                    return of(response);
                }
                return this.saveClientCoordinateParams$(Math.floor(Number(cid))).pipe(
                    map(() => response),
                    catchError((err) => {
                        this.toastr.warning(
                            err?.error?.message ||
                                'Les paramètres des données n\'ont pas pu être enregistrés.',
                            'Attention'
                        );
                        return of(response);
                    })
                );
            }),
            finalize(() => {
                this.isLoading = false;
                this.isSubmitting = false;
                this.cd.markForCheck();
            })
        ).subscribe({
            next: (response: any) => {
                if (response?.__skipSuccess) {
                    this.isSaved = false;
                    return;
                }
                this.isSaved = true;

                const typedResponse: ApiResponse = response;
                const clientToPopulate = typedResponse.client || typedResponse;
                this.populateForm(clientToPopulate as Client);

                this.toastr.success(`Client "${clientData.nom_prenom}" ${this.mode === 'edit' ? 'mis à jour' : 'créé'} avec succès ! `, 'Succès');

                this.currentStep = this.steps.length - 1;

                this.cd.markForCheck();
            },
            error: (error) => {
                this.isSaved = false;
                const errorMessage = error.status === 400
                    ? (error.error?.errors?.map((err: any) => err.message).join(', ') || error.error?.message || 'Données invalides. Veuillez vérifier les champs.')
                    : error.status === 401
                        ? 'Non autorisé. Veuillez vous reconnecter.'
                        : 'Une erreur est survenue lors de la création/mise à jour.';
                this.toastr.error(errorMessage, 'Erreur');
                this.cd.markForCheck();
            }
        });
    }

    addAddress(): void {
        if (this.addressArray.length < 5) {
            this.addressArray = [...this.addressArray, {
                type: null,
                pays: null,
                region: null,
                code_postal: null,
                adresse: null,
                station_id: this.currentStationId,
                user_id: this.currentUserId,
                client_id: this.clientId || 0
            } as Address];
            this.hasAddedAddress = true;
            this.validateStep3();
            this.cd.detectChanges();
            this.refreshFeatherIcons();
        } else {
            this.toasterInfo('Vous ne pouvez pas ajouter plus de 5 adresses.');
        }
    }

    deleteAddress(index: number): void {
        this.addressArray = this.addressArray.filter((_, i) => i !== index);
        this.hasAddedAddress = this.addressArray.length > 0;
        this.validateStep3();
        this.cd.detectChanges();
        this.refreshFeatherIcons();
    }

    addPhone(): void {
        if (this.client.telephones.length < 5) {
            this.client.telephones = [...this.client.telephones, {
                type: null,
                num_tel: null,
                code_pays: '',
                station_id: this.currentStationId,
                user_id: this.currentUserId,
                client_id: this.clientId || 0
            } as Telephone];
            this.hasAddedPhone = true;
            this.validateStep3();
            this.cd.detectChanges();
            this.refreshFeatherIcons();
        } else {
            this.toasterInfo('Vous ne pouvez pas ajouter plus de 5 numéros de téléphone.');
        }
    }

    deletePhone(index: number): void {
        this.client.telephones = this.client.telephones.filter((_, i) => i !== index);
        this.hasAddedPhone = this.client.telephones.length > 0;
        this.validateStep3();
        this.cd.detectChanges();
        this.refreshFeatherIcons();
    }

    addNewProduct(): void {
        this.currentStep = 0;
        this.mode = 'add';
        this.clientId = null;
        this.productData = {
            type: '',
            authorizedOverdraft: null,
            priceType: null,
            paymentPeriod: '',
            paymentPeriodValue: 1,
            customPrices: []
        };
        this.client = {
            type: '',
            civilite: '',
            nom_prenom: '',
            station_id: this.currentStationId,
            user_id: this.currentUserId,
            identifiant_unique: '',
            nom_entreprise: null,
            numero_identification_fiscale: null,
            site_web: null,
            emails: [{ email: '', user_id: this.currentUserId, client_id: 0, station_id: this.currentStationId }],
            is_archive: false,
            telephones: [{
                type: null,
                num_tel: null,
                code_pays: '+216',
                station_id: this.currentStationId,
                user_id: this.currentUserId,
                client_id: 0
            }],
            adresses: [{
                type: null,
                pays: null,
                region: null,
                code_postal: null,
                adresse: null,
                station_id: this.currentStationId,
                user_id: this.currentUserId,
                client_id: 0
            }],
            informations: [{
                encours_atorise: null,
                prix_id: null,
                type_date: null,
                nb_date: null,
                client_id: 0,
                station_id: this.currentStationId,
                user_id: this.currentUserId
            }]
        };
        this.addressArray = this.client.adresses;
        this.hasAddedPhone = true;
        this.hasAddedAddress = true;
        this.isStep1Valid = false;
        this.isStep2Valid = false;
        this.isStep3Valid = true;
        this.isStep4Valid = true;
        this.isStepParamDataValid = true;
        this.paramTableRows = [];
        this.paramSelectOptionsCache.clear();
        this.headerService.setConfig({
            title: 'Ajouter Client',
            buttons: []
        });
        this.router.navigate(['/tiers/clients/add-client']);
        this.cd.detectChanges();
    }

    viewClient(clientId?: number): void {
        const effectiveId = clientId || this.client?.id || this.clientId || this.originalClientId;

        if (!effectiveId || effectiveId <= 0) {
            this.toastr.error(`Navigation impossible : ID client manquant (${effectiveId}). Vérifiez les logs console.`, 'Erreur Critique');
            return;
        }

        this.router.navigate(['/tiers/clients/details', effectiveId, 'detail-contenue-apercue']);
    }

    quickAdd(): void {
        this.validateStep1();
        this.validateStep2();
        if (this.isStep1Valid && this.isStep2Valid && !this.isSubmitting) {
            this.saveClient(false);
        } else {
            this.toastr.error('Veuillez remplir tous les champs requis : type, civilité, nom et prénom.', 'Erreur');
        }
    }

    toasterInfo(msg: any): void {
        this.toastr.info(msg, '', {
            positionClass: 'toast-bottom-right',
            toastClass: 'toast ngx-toastr',
            closeButton: true
        });
    }

    incrementPaymentPeriod(): void {
        this.productData.paymentPeriodValue = (this.productData.paymentPeriodValue || 0) + 1;
        this.validateStep4();
    }

    decrementPaymentPeriod(): void {
        if (this.productData.paymentPeriodValue > 1) {
            this.productData.paymentPeriodValue -= 1;
            this.validateStep4();
        }
    }

    selectClientType(type: string): void {
        this.productData.type = type;
        this.client.type = type === 'Materiel' ? 'Individuel' : 'Professionnel';
        this.showTypeError = false
        this.validateStep1();
        this.refreshFeatherIcons();
    }

    selectAddressType(index: number, type: string): void {
        this.addressArray[index].type = type;
        this.validateStep3();
        this.validatePhoneVisual();
        this.validateAddressVisual();
        this.refreshFeatherIcons();
    }
    confirmPrice(): void {
        if (this.newPriceName.trim() && this.newPriceValue >= 0) {
            const newPrice: Prix = {
                libelle: this.newPriceName.trim(),
                statut: true,
                station_id: this.currentStationId,
                client_id: this.clientId || undefined
            };
            this.isLoading = true;
            this.prixService.addPrix(newPrice, this.currentStationId).subscribe({
                next: (createdPrice) => {

                    if (!createdPrice.id) {
                        this.toastr.error('L\'ID du prix n\'a pas été renvoyé par le serveur.', 'Erreur');
                        this.isLoading = false;
                        this.cd.detectChanges();
                        return;
                    }

                    this.prixList.push(createdPrice);

                    this.productData.customPrices.push({
                        id: uuidv4(),
                        prix_id: createdPrice.id,
                        name: createdPrice.libelle,
                        value: this.newPriceValue
                    });
                    this.productData.priceType = createdPrice.id;

                    if (this.client.informations && this.client.informations.length > 0) {
                        this.client.informations[0].prix_id = createdPrice.id;
                    } else {
                        this.client.informations = [{
                            encours_atorise: null,
                            prix_id: createdPrice.id,
                            type_date: null,
                            nb_date: null,
                            client_id: this.clientId || 0,
                            station_id: this.currentStationId,
                            user_id: this.currentUserId
                        }];
                    }

                    this.closePriceModal();
                    this.validateStep4();
                    this.isLoading = false;
                    this.cd.detectChanges();
                    this.toastr.success('Nouveau prix ajouté avec succès.', 'Succès');
                },
                error: (error) => {
                    this.isLoading = false;
                    this.toastr.error('Erreur lors de l\'ajout du prix personnalisé.', 'Erreur');
                    this.cd.detectChanges();
                }
            });
        } else {
            this.toastr.info('Veuillez remplir le nom et une valeur valide du prix.', 'Information');
        }
    }

    togglePriceType(selectedId: number): void {

        if (this.productData.priceType !== selectedId) {
            this.productData.priceType = selectedId;
        } else {
        }
        if (this.client.informations && this.client.informations.length > 0) {
            this.client.informations[0].prix_id = this.productData.priceType;
        } else {
            this.client.informations = [{
                encours_atorise: null,
                prix_id: this.productData.priceType,
                type_date: null,
                nb_date: null,
                client_id: this.clientId || 0,
                station_id: this.currentStationId,
                user_id: this.currentUserId
            }];
        }

        this.validateStep4();
        this.cd.detectChanges();
    }
    openPriceModal(): void {
        this.newPriceName = '';
        this.newPriceValue = 0;
        this.showPriceModal = true;
    }

    closePriceModal(): void {
        this.showPriceModal = false;
    }

    resetStep2Errors(): void {
        this.step2Errors = {
            civilite: this.step2Errors.civilite,
            nom_prenom: false,
            nom_entreprise: false
        };
    }
    resetStep3Errors(): void {
        this.step3Errors = { phones: {}, addresses: {} };
    }

    hasPhoneData(index: number): boolean {
        const phone = this.client.telephones[index];
        return !!(phone?.type|| phone?.num_tel);
    }

    hasAddressData(index: number): boolean {
        const addr = this.addressArray[index];
        return !!(addr?.type || addr?.pays || addr?.region || addr?.code_postal || addr?.adresse);
    }
    clearPhone(index: number) {
        this.client.telephones[index] = {
            type: '',
            code_pays: '',
            num_tel: ''
        };
        this.validateStep3();
    }

    clearAddress(index: number) {
        this.addressArray[index] = {
            type: '',
            pays: '',
            region: '',
            code_postal: '',
            adresse: ''
        };
        this.validateStep3();
    }

    hasDatePaiementData(): boolean {
        return !!this.productData.paymentPeriod ||
            (this.productData.paymentPeriodValue != null && this.productData.paymentPeriodValue > 0);
    }

    clearDatePaiement(): void {
        this.productData.paymentPeriod = null;
        this.productData.paymentPeriodValue = null;
        this.validateStep4();
    }

    validatePaymentFields(): { isValid: boolean; errorMessage?: string } {
        const hasPeriod = !!this.productData.paymentPeriod;
        const hasValue = this.productData.paymentPeriodValue != null && this.productData.paymentPeriodValue > 0;

        if (hasPeriod && !hasValue) {
            return { isValid: false, errorMessage: 'Veuillez entrer un nombre de jours/mois/années.' };
        }
        if (!hasPeriod && hasValue) {
            return { isValid: false, errorMessage: 'Veuillez sélectionner Jours, Mois ou Année.' };
        }
        if (hasPeriod && hasValue) {
            return { isValid: true };
        }
        return { isValid: true };
    }

    hasListeData(): boolean {
        return !!this.productData.priceType;
    }

    clearListe(): void {
        this.productData.priceType = null;
        this.validateStep4();
    }
    removeOrClearAddress(index: number): void {
        if (this.addressArray.length > 1) {
            this.deleteAddress(index);
        } else {
            this.clearAddress(index);
        }
        delete this.step3Errors.addresses[index]
        this.touchedAddressRows.delete(index);

        this.validateStep3();
        this.cd.detectChanges();
        this.refreshFeatherIcons();
    }
    removeOrClearPhone(index: number): void {
        if (this.client.telephones.length > 1) {
            this.deletePhone(index);
        } else {
            this.clearPhone(index);
        }

        delete this.step3Errors.phones[index];
        this.touchedPhoneRows.delete(index);
        this.validateStep3();
        this.cd.detectChanges();
        this.refreshFeatherIcons();
    }

    private hasAnyPhoneFieldFilled(index: number): boolean {
        const phone = this.client.telephones[index];
        return !!(
            phone?.type ||
            phone?.num_tel ||
            (phone?.code_pays && phone.code_pays.trim() !== '')
        );
    }

    private hasAnyAddressFieldFilled(index: number): boolean {
        const addr = this.addressArray[index];
        return !!(
            addr?.type ||
            addr?.pays ||
            addr?.region ||
            addr?.code_postal ||
            addr?.adresse
        );
    }

    private setPhoneError(index: number, field: 'type' | 'number', message: string): void {
        if (!this.step3Errors.phones[index]) {
            this.step3Errors.phones[index] = {};
        }
        this.step3Errors.phones[index][field] = message;
    }

    private setAddressError(index: number, field: 'type' | 'pays' | 'region' | 'code_postal' | 'adresse', message: string): void {
        if (!this.step3Errors.addresses[index]) {
            this.step3Errors.addresses[index] = {};
        }
        this.step3Errors.addresses[index][field] = message;
    }


}
